import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const empty = { name: "", location: "", description: "", active: true };
const PAGE_SIZE = 12;

export default function Sites() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (p) => {
    try {
      const { data } = await api.get("/admin/sites", { params: { page: p, page_size: PAGE_SIZE } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(page); }, [page]);

  const submit = async () => {
    try {
      if (editing) await api.put(`/admin/sites/${editing}`, form);
      else await api.post("/admin/sites", form);
      toast.success("Saved");
      setOpen(false);
      await load(page);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this site?")) return;
    try {
      await api.delete(`/admin/sites/${id}`);
      toast.success("Deleted");
      await load(page);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <PageHeader
        testId="sites-header"
        title="Hotspot Sites"
        subtitle="Physical locations hosting your routers"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-site-btn"
                onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
                className="h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
              >
                <Plus className="h-4 w-4 mr-1" /> NEW SITE
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? "Edit Site" : "New Site"}</DialogTitle>
                <DialogDescription className="text-nhp-muted">Name and location of the hotspot venue.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Name</Label>
                  <Input data-testid="site-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Location</Label>
                  <Input data-testid="site-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none" />
                </div>
                <div className="flex items-center justify-between border border-nhp-border px-3 py-2">
                  <Label>Active</Label>
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button data-testid="site-save-btn" onClick={submit} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">SAVE</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-4 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center text-sm text-nhp-muted py-16">No sites yet.</div>
        )}
        {items.map((s) => (
          <div key={s.id} data-testid={`site-card-${s.id}`} className="border border-nhp-border bg-nhp-surface p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 grid place-items-center border border-nhp-border bg-nhp-bg">
                  <MapPin className="h-4 w-4 text-nhp-primary" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-heading font-bold text-lg">{s.name}</div>
                  <div className="text-sm text-nhp-muted">{s.location}</div>
                </div>
              </div>
              <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${s.active ? "text-nhp-primary border-nhp-primary/40" : "text-nhp-muted border-nhp-border"}`}>
                {s.active ? "LIVE" : "OFF"}
              </span>
            </div>
            {s.description && <p className="text-sm text-nhp-muted mt-3 line-clamp-2">{s.description}</p>}
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-nhp-border">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Routers</div>
                <div className="font-mono font-bold text-lg mt-1">{s.router_count}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Active</div>
                <div className="font-mono font-bold text-lg mt-1 text-nhp-primary">{s.active_sessions}</div>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-3">
              <Button size="sm" variant="ghost" onClick={() => { setEditing(s.id); setForm({ name: s.name, location: s.location, description: s.description || "", active: s.active }); setOpen(true); }} className="h-8 rounded-none text-nhp-muted hover:text-nhp-text">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="h-8 rounded-none text-nhp-muted hover:text-nhp-critical">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="border-x border-b border-nhp-border bg-nhp-surface">
        <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="sites" onChange={setPage} testId="sites" />
      </div>
      </div>
    </div>
  );
}
